import { useLogout } from '@myrentalmate/api-client';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router';

import { displayName, initialsOf, type Me } from '../lib/me';
import { useViews } from '../lib/useViews';

/** The signed-in person's initials in the top bar, opening Profile, any other views, and Logout. */
export function UserMenu({ me }: { me: Me }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const logout = useLogout({
    mutation: { onSettled: () => window.location.assign('/') },
  });
  const close = () => setAnchor(null);
  const views = useViews(me);

  return (
    <>
      <Tooltip title={displayName(me)}>
        <IconButton
          aria-label="User menu"
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
          sx={{ p: 0.5 }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: '0.9rem',
              fontWeight: 600,
              bgcolor: 'secondary.main',
              color: 'primary.dark',
            }}
          >
            {initialsOf(me)}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {me.display_name || 'Add your name in Profile'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {me.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem component={Link} to="/account" onClick={close}>
          Profile
        </MenuItem>
        {views.length > 0 && <Divider />}
        {views.length > 0 && (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', px: 2, pt: 0.5 }}
          >
            Switch view
          </Typography>
        )}
        {views.map((view) => (
          <MenuItem
            key={view.key}
            selected={view.isCurrent}
            onClick={() => {
              close();
              void view.choose();
            }}
          >
            {view.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled={logout.isPending} onClick={() => logout.mutate()}>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}
